import { useParams } from "react-router-dom";
import FormPage from "../../components/forms/FormPage";
import { Version } from "../../types/version";
import useVersionController from "../../controllers/versionController";
import FormEntitySelectionField from "../../components/forms/fields/basic/EntitySelectionField";
import renderWorkSelection from "../../components/entitiesReferences/SelectionModals/WorkSelectionModal";
import FormSelectField from "../../components/forms/fields/basic/SelectField";
import FormTextField from "../../components/forms/fields/basic/TextField";
import { SystemEntityCollections } from "../../shared/enums/shared-enums";
import { IntellexusDateField } from "../../components/forms/common/intellexusDate/IntellexusDateField";
import LinksListField from "../../components/forms/common/subForms/LinksListField";
import TextTable from "../../components/tables/TextTable";
import useVersionLogic from "../../hooks/logic/versionLogic";
import TitleList from "./components/titleEvidence/TitleList";
import ContributorSection from "./sections/ContributorSection";
import VersionTeamSection from "./sections/VersionTeamSection";
import ReferenceVersionSection from "./sections/ReferenceVersionSection";
import { SystemLanguages } from "../../shared/enums/shared-enums";
import { VersionTeamConnection } from "../../types/versionTeam";

interface AddEditVersionPageProps {
  systemLanguageType: Version["system_language_type"];
}

const AddEditVersionPage: React.FC<AddEditVersionPageProps> = ({
  systemLanguageType,
}) => {
  const { _id = null } = useParams<{
    _id?: string;
  }>();

  const controller = useVersionController(_id || null, systemLanguageType);
  const formController = { ...controller, title: controller.editTitle };
  const {
    completenessEnum,
    versionCollectionEnum,
    versionSourceLanguageEnum,
    versionLinkToOtherDatabasesEnum,
    transferedCollectionEnum,
    doxographicalCategoryEnum,
    languageEnum,
    statusOfIndicOriginEnum,
    showTibetanFields,
    showField,
  } = useVersionLogic(systemLanguageType);

  return (
    <FormPage<Version>
      controller={formController}
      sections={(versionData) => {
        const filters = {
          version_id: versionData._key,
          system_language_type: systemLanguageType!,
        };

        return [
          <div className="edit-file-container-table" key="texts">
            <h3 className="section-title">Texts</h3>
            <div className="edge-actions">
              <div className="table-section">
                <TextTable
                  filters={filters}
                  enableRowActions={false}
                  // Every row belongs to this version; the column would repeat it.
                  hiddenColumnIds={["version"]}
                />
              </div>
            </div>
          </div>,
          <ContributorSection
            key="contributors"
            anchor={versionData}
            systemLanguageType={systemLanguageType}
          />,
          ...(systemLanguageType === SystemLanguages.tibetan ||
          systemLanguageType === SystemLanguages.chinese
            ? [
                <VersionTeamSection
                  key="versionTeams"
                  anchor={versionData}
                  systemLanguageType={
                    systemLanguageType as VersionTeamConnection["system_language_type"]
                  }
                />,
              ]
            : []),
          <ReferenceVersionSection
            key="referenceVersions"
            anchor={versionData}
            systemLanguageType={systemLanguageType}
          />,
        ];
      }}
    >
      <FormTextField name="name" label="Name" placeholder="Name" />
      <FormEntitySelectionField
        name="work"
        label="Work"
        placeholder="Select a work"
        entityCollection={SystemEntityCollections.WORK}
        renderEntitySelection={renderWorkSelection(systemLanguageType)}
      />
      <FormSelectField
        name="completeness"
        label="Completeness"
        placeholder="Select completeness"
        useEnum={completenessEnum}
      />
      <IntellexusDateField name={"version_date"} label={"Version Date"} />
      {showField("date_of_composition") && (
        <IntellexusDateField
          name="date_of_composition"
          label="Date of Composition"
        />
      )}
      {showField("link_to_other_databases") && (
        <LinksListField
          name="link_to_other_databases"
          typeEnum={versionLinkToOtherDatabasesEnum}
          label="Links to Other Databases"
          rowLabel="Link"
          typeLabel="Link Type"
          valueLabel="Link"
          typePlaceHolder="Select link type"
          valuePlaceHolder="Enter Link"
        />
      )}
      {showField("title") && (
        <TitleList name="title" label="Titles" rowLabel="Title" />
      )}
      {showField("doxographical_category") && (
        <FormSelectField
          name="doxographical_category"
          label="Doxographical Category"
          placeholder="Select doxographical category"
          useEnum={doxographicalCategoryEnum}
        />
      )}
      {showField("transfered_collection") && (
        <FormSelectField
          name="transfered_collection"
          label="Transfered Collection"
          placeholder="Select transfered collection"
          useEnum={transferedCollectionEnum}
        />
      )}
      {showField("catalog") && (
        <>
          <FormTextField
            name="catalog.catalog_no"
            label="Catalog Number"
            placeholder="Enter catalog number"
          />
          <FormTextField
            name="catalog.catalog_entry"
            label="Catalog Entry"
            placeholder="Enter catalog entry"
          />
        </>
      )}
      {showField("language") && (
        <FormSelectField
          name="language"
          label="Language"
          useEnum={languageEnum}
          multiple
          placeholder="Select Language"
        />
      )}
      {showField("status_of_indic_origin") && (
        <FormSelectField
          name="status_of_indic_origin"
          label="Status of Indic Origin"
          useEnum={statusOfIndicOriginEnum}
          multiple
          placeholder="Select Status of Indic Origin"
        />
      )}
      {showTibetanFields && (
        <>
          <FormSelectField
            name="collection"
            label="Collection"
            placeholder="Select collection"
            useEnum={versionCollectionEnum}
          />
          <FormSelectField
            name="source_language"
            label="Source Language"
            placeholder="Select source language"
            useEnum={versionSourceLanguageEnum}
          />
          <FormTextField
            name="vol_number"
            label="Volume Number"
            placeholder="Enter volume number"
          />
          <FormTextField
            name="vol_name"
            label="Volume Name"
            placeholder="Enter volume name"
          />
          <FormTextField
            name="folio_start"
            label="Folio/Page Start"
            placeholder="Enter folio/page start"
          />
          <FormTextField
            name="folio_end"
            label="Folio/Page End"
            placeholder="Enter folio/page end"
          />
        </>
      )}
    </FormPage>
  );
};

export default AddEditVersionPage;
